import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Edit3, Save, X, Loader2, Eye, Check, Info, MapPin, Globe, Heart, Star, Target,
  ChevronDown, ChevronUp, Lock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { showToast } from '../../utils/errorHandler';
import { mapLevelValues } from '../../utils/levelMapper';
import { mapExplorerPrefsFromApi } from '../../utils/profilePrefs';

interface PrefsProfile {
  career_status: string | null;
  base_locations: string | null;
  preferred_locations: string | null;
  role_preferences: string | null;
  current_role: string | null;
  current_level: string | null;
  is_contactable: boolean;
  real_talk_sentence: string | null;
  real_talk_tags: string[] | null;
  cv_id: string | null;
}

interface CvPassportPrefsProps {
  /** Read-only render (e.g. recruiter preview) — no edit affordances. */
  readOnly?: boolean;
  /** Notify parent (MyPassport) when prefs were saved (e.g. to refresh contactability). */
  onSaved?: (p: PrefsProfile) => void;
}

// Same 6-minute lock the CV upload pipeline uses. Editing prefs while a CV is
// still processing collides with re-embedding, so we block it (matches Profile).
const checkUploadInProgress = (): number => {
  const ts = localStorage.getItem('cv_upload_timestamp');
  if (!ts) return 0;
  try {
    const elapsed = Date.now() - parseInt(ts, 10);
    const remaining = 6 * 60 * 1000 - elapsed;
    if (remaining > 0) return Math.ceil(remaining / 60000);
    localStorage.removeItem('cv_upload_timestamp');
  } catch {
    localStorage.removeItem('cv_upload_timestamp');
  }
  return 0;
};

const emptyForm = {
  career_status: 'invisible',
  base_locations: '',
  preferred_locations: '',
  role_preferences: '',
  current_role: '',
  current_level: '',
  is_contactable: false,
  real_talk_sentence: '',
  real_talk_tags: [] as string[],
};

const CvPassportPrefs: React.FC<CvPassportPrefsProps> = ({ readOnly = false, onSaved }) => {
  const { t, i18n } = useTranslation(['passport', 'profile', 'inspire']);
  const { user, apiCall } = useAuth();
  const currentLanguage = i18n.language?.startsWith('de') ? 'de' : 'en';

  const [profile, setProfile] = useState<PrefsProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  const [levelOpen, setLevelOpen] = useState(false);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const levelRef = useRef<HTMLDivElement>(null);

  const careerStatusOptions = useMemo(() => [
    { value: 'actively_seeking', label: t('profile:careerStatus.options.actively_seeking') },
    { value: 'curious', label: t('profile:careerStatus.options.curious') },
    { value: 'invisible', label: t('profile:careerStatus.options.invisible') },
  ], [t]);

  const levelOptions = useMemo(() => [
    t('inspire:levels.highSchool'), t('inspire:levels.apprentice'), t('inspire:levels.bachelor'),
    t('inspire:levels.master'), t('inspire:levels.workingStudent'), t('inspire:levels.phd'),
    t('inspire:levels.qualifiedWorker'), t('inspire:levels.entrepreneur'), t('inspire:levels.junior'),
    t('inspire:levels.midLevel'), t('inspire:levels.senior'), t('inspire:levels.masterCraftsman'),
    t('inspire:levels.lead'), t('inspire:levels.expert'),
  ], [t]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (levelRef.current && !levelRef.current.contains(e.target as Node)) setLevelOpen(false);
    };
    if (levelOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [levelOpen]);

  useEffect(() => {
    if (editing) setForm(prev => ({ ...prev, current_level: selectedLevels.join(', ') }));
  }, [selectedLevels, editing]);

  const hydrate = (p: PrefsProfile) => {
    const mapped = mapExplorerPrefsFromApi(p);
    setForm({
      ...mapped,
      real_talk_sentence: p.real_talk_sentence || '',
      real_talk_tags: p.real_talk_tags || [],
    });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.userId) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const res = await apiCall(`${apiBase}/api/v1/profiles/${user.userId}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        setProfile(data);
        hydrate(data);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const startEdit = () => {
    const minutesLeft = checkUploadInProgress();
    if (minutesLeft > 0) {
      showToast(t('profile:cv.uploadWait', { minutes: minutesLeft }), 'info');
      return;
    }
    if (profile) hydrate(profile);
    const dbLevels = (profile?.current_level || '').split(',').map(l => l.trim()).filter(Boolean);
    setSelectedLevels(mapLevelValues(dbLevels, currentLanguage));
    setEditing(true);
  };

  const cancelEdit = () => {
    if (profile) hydrate(profile);
    setEditing(false);
    setLevelOpen(false);
  };

  const save = async () => {
    const minutesLeft = checkUploadInProgress();
    if (minutesLeft > 0) {
      showToast(t('profile:cv.uploadWait', { minutes: minutesLeft }), 'info');
      return;
    }
    try {
      setSaving(true);
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      // Only the fields owned by this page — is_contactable / real_talk now live
      // on the Real Talk page, so we never overwrite them from here.
      const payload = {
        career_status: form.career_status,
        base_locations: form.base_locations,
        preferred_locations: form.preferred_locations,
        role_preferences: form.role_preferences,
        current_role: form.current_role,
        current_level: form.current_level,
      };
      const res = await apiCall(`${apiBase}/api/v1/profiles/${user?.userId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(60000),
      });
      if (res.ok) {
        const updated = await res.json();
        setProfile(updated);
        hydrate(updated);
        setEditing(false);
        setLevelOpen(false);
        showToast(t('passport:prefs.savedToast'), 'success');
        onSaved?.(updated);
      } else {
        showToast(t('profile:contactInfo.error', 'Failed to update. Please try again.'), 'error');
      }
    } catch (e: any) {
      showToast(t('profile:contactInfo.error', 'Failed to update. Please try again.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> …
      </div>
    );
  }

  const visibilityLabel = careerStatusOptions.find(o => o.value === (profile?.career_status || 'invisible'))?.label;
  const canEdit = !readOnly && !editing;

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 space-y-4">
      {/* Recruiter-only banner */}
      <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
        <Lock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] sm:text-xs text-amber-800 leading-relaxed">{t('passport:recruiterOnly.hint')}</p>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm sm:text-base font-semibold text-slate-800">{t('passport:prefs.title')}</h3>
          <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">{t('passport:prefs.subtitle')}</p>
        </div>
        {canEdit && (
          <button
            onClick={startEdit}
            className="flex items-center gap-1 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 text-pink-600 hover:text-pink-700 hover:bg-pink-100 rounded-lg transition-colors flex-shrink-0"
          >
            <Edit3 className="w-3.5 h-3.5" />
            {t('passport:common.edit')}
          </button>
        )}
      </div>

      {!editing ? (
        /* ─── View mode ─── */
        <div className="space-y-4">
          <Field icon={<Eye className="w-3.5 h-3.5 text-pink-600" />} label={t('passport:prefs.visibilityLabel')}>
            {visibilityLabel}
          </Field>

          {/* Job preferences */}
          <div className="pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field icon={<MapPin className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.baseLocations')}>
              {profile?.base_locations || t('profile:jobPreferences.notSpecified')}
            </Field>
            <Field icon={<Globe className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.preferredLocations')}>
              {profile?.preferred_locations || t('profile:jobPreferences.notSpecified')}
            </Field>
            <Field icon={<Heart className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.rolePreferences')}>
              {profile?.role_preferences || t('profile:jobPreferences.notSpecified')}
            </Field>
            <Field icon={<Star className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.currentRole')}>
              {profile?.current_role || t('profile:jobPreferences.notSpecified')}
            </Field>
            <Field icon={<Target className="w-3.5 h-3.5 text-pink-600" />} label={t('profile:jobPreferences.currentLevel')}>
              {profile?.current_level || t('profile:jobPreferences.notSpecified')}
            </Field>
          </div>
        </div>
      ) : (
        /* ─── Edit mode ─── */
        <div className="space-y-4">
          {/* Visibility */}
          <div>
            <label className="text-xs text-slate-500 flex items-center gap-1.5 mb-2">
              <Eye className="w-3.5 h-3.5 text-pink-600" /> {t('passport:prefs.visibilityLabel')}
            </label>
            <div className="space-y-2">
              {careerStatusOptions.map(option => (
                <label
                  key={option.value}
                  className={`flex items-center px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                    form.career_status === option.value ? 'border-pink-500 bg-pink-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="career_status"
                    value={option.value}
                    checked={form.career_status === option.value}
                    onChange={(e) => setForm({ ...form, career_status: e.target.value })}
                    className="sr-only"
                  />
                  <span className="flex-1 text-sm text-slate-700">{option.label}</span>
                  {form.career_status === option.value && <Check className="w-4 h-4 text-pink-500 flex-shrink-0" />}
                </label>
              ))}
            </div>
            {form.career_status !== 'invisible' && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 mt-2">
                <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-800 leading-relaxed">{t('profile:careerStatus.atsHint')}</p>
              </div>
            )}
          </div>

          {/* Job preferences */}
          <div className="space-y-3 pt-3 border-t border-slate-100">
            <EditField label={t('profile:jobPreferences.baseLocations')} icon={<MapPin className="w-3.5 h-3.5 text-pink-600" />}>
              <input
                type="text"
                value={form.base_locations}
                onChange={(e) => setForm({ ...form, base_locations: e.target.value })}
                placeholder={t('profile:jobPreferences.baseLocationsPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
            </EditField>
            <EditField label={t('profile:jobPreferences.preferredLocations')} icon={<Globe className="w-3.5 h-3.5 text-pink-600" />}>
              <input
                type="text"
                value={form.preferred_locations}
                onChange={(e) => setForm({ ...form, preferred_locations: e.target.value })}
                placeholder={t('profile:jobPreferences.preferredLocationsPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
            </EditField>
            <EditField label={t('profile:jobPreferences.rolePreferences')} icon={<Heart className="w-3.5 h-3.5 text-pink-600" />}>
              <input
                type="text"
                value={form.role_preferences}
                onChange={(e) => setForm({ ...form, role_preferences: e.target.value })}
                placeholder={t('profile:jobPreferences.rolePreferencesPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
            </EditField>
            <EditField label={t('profile:jobPreferences.currentRole')} icon={<Star className="w-3.5 h-3.5 text-pink-600" />}>
              <input
                type="text"
                value={form.current_role}
                onChange={(e) => setForm({ ...form, current_role: e.target.value })}
                placeholder={t('profile:jobPreferences.currentRolePlaceholder')}
                maxLength={200}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
            </EditField>
            <EditField label={t('profile:jobPreferences.currentLevel')} icon={<Target className="w-3.5 h-3.5 text-pink-600" />}>
              <div className={`relative ${levelOpen ? 'z-30' : ''}`} ref={levelRef}>
                <button
                  type="button"
                  onClick={() => setLevelOpen(!levelOpen)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white text-left pr-10"
                >
                  <span className={selectedLevels.length === 0 ? 'text-slate-400' : 'text-slate-700'}>
                    {selectedLevels.length === 0
                      ? t('profile:jobPreferences.currentLevelPlaceholder')
                      : selectedLevels.length === 1
                      ? selectedLevels[0]
                      : t('profile:jobPreferences.levelsSelected', { count: selectedLevels.length })}
                  </span>
                  {levelOpen
                    ? <ChevronUp className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    : <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
                </button>
                {levelOpen && (
                  <div className="absolute z-30 bottom-full mb-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {levelOptions.map(option => (
                      <label key={option} className="flex items-center px-3 py-2 hover:bg-pink-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedLevels.includes(option)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedLevels([...selectedLevels, option]);
                            else setSelectedLevels(selectedLevels.filter(l => l !== option));
                          }}
                          className="mr-2 h-4 w-4 text-pink-600 focus:ring-pink-400 border-slate-300 rounded"
                        />
                        <span className="text-sm text-slate-700">{option}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </EditField>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-3 border-t border-slate-100">
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 transition-all shadow-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t('passport:common.saving') : t('passport:common.save')}
            </button>
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              <X className="w-4 h-4" />
              {t('passport:common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div>
    <label className="text-[11px] sm:text-xs text-slate-500 flex items-center gap-1.5">{icon}{label}</label>
    <p className="text-xs sm:text-sm text-slate-700 mt-0.5">{children}</p>
  </div>
);

const EditField: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div>
    <label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">{icon}{label}</label>
    {children}
  </div>
);

export default CvPassportPrefs;
